import { Radio } from "@base-ui/react/radio";
import { RadioGroup } from "@base-ui/react/radio-group";
import { Settings2 } from "lucide-react";
import { useEffect, useId, useState } from "react";

import { CoreSkillToggle } from "@/components/shared/CoreSkillToggle";
import { GroupCombobox } from "@/components/shared/GroupCombobox";
import {
    OrganizationCombobox,
    type EntityComboboxValidity,
} from "@/components/shared/OrganizationCombobox";
import { Button } from "@/components/ui/button";
import {
    Popover,
    PopoverContent,
    PopoverTrigger,
} from "@/components/ui/popover";
import { cn } from "@/lib/utils";
import type { SkillEditorScopeKind } from "../skill-editor-state";

const SCOPE_OPTIONS: readonly {
    value: SkillEditorScopeKind;
    label: string;
}[] = [
    { value: "global", label: "Global" },
    { value: "organization", label: "Organization" },
    { value: "group", label: "Group" },
];

export const GROUP_CORE_COUNT_MESSAGE =
    "Core skill slots are counted separately in each organization.";

export interface SkillSettingsPopoverProps {
    scopeKind: SkillEditorScopeKind;
    onScopeKindChange: (scopeKind: SkillEditorScopeKind) => void;
    organizationId: number | null;
    onOrganizationChange: (organizationId: number | null) => void;
    organizationGroupId: number | null;
    onOrganizationGroupChange: (organizationGroupId: number | null) => void;
    onScopeValidityChange?: (validity: EntityComboboxValidity) => void;
    isCore: boolean;
    onIsCoreChange: (next: boolean) => void;
    coreCount?: number;
    coreCountScope?: "org" | "global";
    coreIncludesThisSkill?: boolean;
    coreError?: string;
    isSaving?: boolean;
    isDirty?: boolean;
}

export function SkillSettingsPopover({
    scopeKind,
    onScopeKindChange,
    organizationId,
    onOrganizationChange,
    organizationGroupId,
    onOrganizationGroupChange,
    onScopeValidityChange,
    isCore,
    onIsCoreChange,
    coreCount,
    coreCountScope = "org",
    coreIncludesThisSkill = false,
    coreError,
    isSaving = false,
    isDirty = false,
}: SkillSettingsPopoverProps) {
    const scopeLabelId = useId();
    const [hasOpened, setHasOpened] = useState(false);
    const isGroupScope = scopeKind === "group";

    useEffect(() => {
        if (scopeKind === "global") onScopeValidityChange?.("valid");
    }, [onScopeValidityChange, scopeKind]);

    return (
        <Popover onOpenChange={(open) => open && setHasOpened(true)}>
            <PopoverTrigger
                render={
                    <Button
                        type="button"
                        variant="outline"
                        size="icon"
                        title="Skill settings"
                        aria-label={
                            isDirty
                                ? "Skill settings, unsaved changes"
                                : "Skill settings"
                        }
                        className="relative"
                    >
                        <Settings2 />
                        {isDirty && (
                            <span
                                aria-hidden="true"
                                className="bg-brand-copper absolute -top-0.5 -right-0.5 size-2 rounded-full"
                            />
                        )}
                    </Button>
                }
            />
            <PopoverContent
                align="end"
                keepMounted
                className="w-80 max-w-[calc(100vw-2rem)] space-y-4"
            >
                <div className="space-y-1.5">
                    <span
                        id={scopeLabelId}
                        className="text-foreground block text-xs font-medium"
                    >
                        Scope
                    </span>
                    <RadioGroup
                        aria-labelledby={scopeLabelId}
                        value={scopeKind}
                        onValueChange={(next) =>
                            onScopeKindChange(next as SkillEditorScopeKind)
                        }
                        disabled={isSaving}
                        className="bg-muted text-muted-foreground grid w-full gap-[3px] rounded-lg p-[3px] max-[400px]:grid-cols-1 min-[401px]:grid-cols-3"
                    >
                        {SCOPE_OPTIONS.map((option) => (
                            <Radio.Root
                                key={option.value}
                                value={option.value}
                                className={cn(
                                    "flex h-[26px] cursor-pointer items-center justify-center rounded-md px-2 text-xs font-medium",
                                    "transition-colors duration-150 ease-out motion-reduce:transition-none",
                                    "focus-visible:ring-ring/50 focus-visible:ring-[3px] focus-visible:outline-none",
                                    "data-checked:bg-background data-checked:text-foreground data-checked:shadow-sm",
                                    "data-disabled:cursor-not-allowed data-disabled:opacity-50"
                                )}
                            >
                                {option.label}
                            </Radio.Root>
                        ))}
                    </RadioGroup>
                    {hasOpened && scopeKind === "organization" && (
                        <div className="[&_[data-slot=combobox-trigger]]:w-full">
                            <OrganizationCombobox
                                mode="entity"
                                value={organizationId}
                                onChange={onOrganizationChange}
                                onValidityChange={onScopeValidityChange}
                                disabled={isSaving}
                                required
                            />
                        </div>
                    )}
                    {hasOpened && isGroupScope && (
                        <div className="[&_[data-slot=combobox-trigger]]:w-full">
                            <GroupCombobox
                                value={organizationGroupId}
                                onChange={onOrganizationGroupChange}
                                onValidityChange={onScopeValidityChange}
                                disabled={isSaving}
                                required
                            />
                        </div>
                    )}
                </div>

                <CoreSkillToggle
                    isCore={isCore}
                    onChange={onIsCoreChange}
                    loading={isSaving}
                    error={coreError}
                    coreCount={coreCount}
                    coreCountScope={coreCountScope}
                    coreCountMessage={
                        isGroupScope ? GROUP_CORE_COUNT_MESSAGE : undefined
                    }
                    includesThisSkill={coreIncludesThisSkill}
                    compact
                />
            </PopoverContent>
        </Popover>
    );
}
